import * as React from 'react';
import { EmailAddress, Attachment } from '@atlassian/cssxp-types-email';
import className from 'classnames';
import mimeTypes from 'mime-types';
import filesize from 'filesize';
import escapeStringRegexp from 'escape-string-regexp';
import { FormattedDate } from '@css-xp/base';
import DOMPurify from 'dompurify';
import PdfIcon from '@css-xp/base/assets/img/application/pdf.png';
import './email-preview.scss';
import { GlobalPageLoadExperience } from '@atlassian/ufo';
import { UFOEventSource } from '@atlassian/temp-observability';

interface Props {
  emailDetails: any;
}

const Address = ({ address, name, addonContent }: { address: string; name?: string; addonContent?: JSX.Element }) => {
  return (
    <div>
      {name ? `${name} (${address})` : address} {addonContent}
    </div>
  );
};

interface AddressFieldProps {
  addresses: EmailAddress[];
}

const AddressField = (props: AddressFieldProps) => {
  const addresses = props.addresses;
  const allAddresses = addresses.map(({ address = '', name }) => {
    return <Address key={address} name={name} address={address} />;
  });

  return <div>{allAddresses}</div>;
};

const ToAddressField = (
  props: AddressFieldProps & { setShowMore: React.Dispatch<React.SetStateAction<boolean>>; showMore: boolean }
) => {
  const { addresses, showMore, setShowMore } = props;
  const totalAddresses = addresses.length;

  if (totalAddresses === 0) return null;

  const firstAddress = addresses[0];

  const { address, name } = firstAddress;

  const hasSingleToAddress = totalAddresses === 1;

  const showMoreButton = (
    <a
      onClick={() => setShowMore((prevShowMore) => !prevShowMore)}
      style={{ cursor: 'pointer' }}
      data-testId="show-more-email"
    >
      {!showMore && <span>+{totalAddresses - 1} more</span>}
      {showMore && <span>Hide</span>}
    </a>
  );

  const [, ...remainingAddresses] = addresses;

  return (
    <div>
      <Address
        key={address}
        name={name}
        address={address}
        addonContent={!hasSingleToAddress ? <>&nbsp; {showMoreButton}</> : undefined}
      />
      {showMore && <AddressField addresses={remainingAddresses} />}
    </div>
  );
};

const EmailPreview = (props: Props) => {
  const isAttachmentDownloadable = (attachment: Attachment) => attachment.contentDisposition === 'attachment';
  const [showMore, setShowMore] = React.useState<boolean>(false);

  const isAttachmentInline = (attachment: Attachment) => attachment.contentDisposition === 'inline';

  const getAttachmentLink = (attachmentId: string) => `/api/1/emails/attachments/${attachmentId}`;

  const EMBEDDED_EMAIL_MIME_TYPE = 'message/rfc822';

  const getAttachmentFilename = (attachment: Attachment) => {
    if (attachment.filename) {
      return attachment.filename;
    }

    const defaultFilename = attachment.contentType === EMBEDDED_EMAIL_MIME_TYPE ? 'message' : 'attachment';
    const ext = mimeTypes.extension(attachment.contentType) || 'unknown';

    return `${defaultFilename}.${ext}`;
  };

  /**
   * GLobal Page Load experience for Email Page
   */
  GlobalPageLoadExperience.startPageLoad(UFOEventSource.EMAIL_SCREEN);
  React.useEffect(() => {
    GlobalPageLoadExperience.success({ force: true });
  }, []);

  const transformHtmlBody = (html: string, inlineAttachments: Attachment[]) => {
    let transformedHtml = html;

    inlineAttachments.forEach(({ contentId = '', id }) => {
      if (contentId.startsWith('<')) {
        contentId = contentId.substring(1, contentId.length - 1);
      }

      const cidPattern = new RegExp(escapeStringRegexp(`cid:${contentId}`), 'g');

      transformedHtml = transformedHtml.replace(cidPattern, getAttachmentLink(id));
    });

    return transformedHtml;
  };

  const transformTextBody = (text: string) => text.replace(/\n/g, '<br>');
  const renderHeaderField = (fieldName: string, content: React.ReactNode): React.ReactNode => {
    const fieldIdentifier = `email-header-field-${fieldName.toLowerCase().replace(/\s/g, '-')}`;
    const headerClasses = className('email-header-field', fieldIdentifier);

    return (
      <div className={headerClasses} data-testid={fieldIdentifier}>
        <div className="email-header-field-name">{fieldName}</div>
        <div className="email-header-field-content" data-testId="email-content">
          {content}
        </div>
      </div>
    );
  };

  const renderAttachments = (): React.ReactNode[] => {
    const { attachments = [] } = props.emailDetails;

    const downloadableAttachments = attachments.filter(isAttachmentDownloadable);

    if (downloadableAttachments.length === 0) {
      return [];
    }

    return downloadableAttachments.map((attachment: any) => {
      const { id, contentType, size } = attachment;

      const filename = getAttachmentFilename(attachment);
      return (
        <div key={id} className="attachment-item">
          {/*// @ts-ignore */}
          {<img src={PdfIcon} className="avatar medium not-round" />}
          <a
            href={
              window.location.host.indexOf('local') === -1
                ? `/css-xp-ans${getAttachmentLink(id)}`
                : getAttachmentLink(id)
            }
            target="_blank"
            rel="noopener noreferrer"
          >{`${filename} (${filesize(size, {
            round: 0,
          })})`}</a>
        </div>
      );
    });
  };

  const renderEmailHeader = () => {
    const { subject = 'No subject', date, from, to, cc, replyTo } = props.emailDetails;

    const toAddressField = <ToAddressField addresses={to} showMore={showMore} setShowMore={setShowMore} />;

    return (
      <div>
        <div className="email-header">
          {from && renderHeaderField('From', <AddressField addresses={from} />)}
          {to && renderHeaderField('To', toAddressField)}
          {cc && renderHeaderField('CC', <AddressField addresses={cc} />)}
          {replyTo && renderHeaderField('Reply To', <AddressField addresses={replyTo} />)}
          {date && renderHeaderField('Date', <FormattedDate date={date} showTime={true} />)}
        </div>
        <h3 className="email-subject">{subject}</h3>
        {renderAttachments()}
      </div>
    );
  };

  const renderEmailBody = () => {
    const { html, text = 'No e-mail body', attachments = [] } = props.emailDetails;

    const inlineAttachments = attachments.filter(isAttachmentInline);

    const body = html !== undefined ? transformHtmlBody(html, inlineAttachments) : transformTextBody(text);

    return (
      <div
        data-testid="email-body"
        className="email-body"
        dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(body) }}
      />
    );
  };
  return (
    <div className="preview-panel email-preview-container">
      {renderEmailHeader()}
      {renderEmailBody()}
    </div>
  );
};

export default EmailPreview;
